import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Radar, Search, Satellite, Clock3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LiveMap from "@/components/LiveMap";
import StopTimeline, { sourceLabel } from "@/components/StopTimeline";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtTime } from "@/lib/format";

function TrackForm({ initial, err }) {
  const nav = useNavigate();
  const [code, setCode] = useState(initial || "");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) nav(`/track/${code.trim().toUpperCase()}`); }} className="card-soft mx-auto max-w-lg space-y-4 p-6" data-testid="track-form">
      <div className="flex items-center gap-3"><span className="grid h-12 w-12 place-items-center rounded-2xl bg-blue-50 text-[#0052FF]"><Radar className="h-6 w-6" /></span>
        <div><h1 className="font-display text-2xl font-extrabold text-[#182678]">Track my bus</h1><p className="text-sm text-slate-500">Enter the booking code from your ticket SMS.</p></div></div>
      <div className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. GPAB12CD" className="h-12 font-mono uppercase tracking-widest" data-testid="track-code-input" />
        <Button type="submit" className="h-12 rounded-xl bg-[#0052FF] px-5 hover:bg-[#0041CC]" data-testid="track-submit-button"><Search className="h-4 w-4" />Track</Button>
      </div>
      {err && <p className="text-sm font-semibold text-rose-600" data-testid="track-error">{err}</p>}
    </form>
  );
}

function Summary({ d }) {
  const board = d.timeline[d.from_idx];
  const off = d.timeline[d.to_idx];
  return (
    <div className="card-soft space-y-3 p-5" data-testid="track-summary">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-sm font-bold tracking-widest">{d.code}</span>
        <span className={`chip ${d.position?.source === "gps" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="track-source"><Satellite className="h-3.5 w-3.5" />{sourceLabel(d.position)}</span>
      </div>
      <div className="font-display text-2xl font-extrabold">{d.from_city} → {d.to_city}</div>
      <div className="text-sm text-slate-500">{fmtDate(d.departure)} · Bus {d.bus_number} ({d.bus_type}) · route {d.route}</div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-xl bg-blue-50 p-3"><div className="label-xs">Your boarding · {board.city}</div><b data-testid="track-board-eta">{board.actual ? `Bus arrived ${fmtTime(board.actual)}` : `${d.from_idx === 0 ? "Departs" : "Expected"} ${fmtTime(board.eta)}`}</b></div>
        <div className="rounded-xl bg-slate-50 p-3"><div className="label-xs">Arrival · {off.city}</div><b data-testid="track-alight-eta">{off.actual ? `Arrived ${fmtTime(off.actual)}` : `~${fmtTime(off.eta)}`}</b></div>
      </div>
      {d.delay_min > 5 && <div className="flex items-center gap-2 rounded-xl bg-rose-50 p-3 text-sm font-semibold text-rose-700" data-testid="track-delay"><Clock3 className="h-4 w-4" />Running about {d.delay_min} min late</div>}
      {!d.live && <p className="text-xs text-slate-500" data-testid="track-not-live">Live tracking starts 45 minutes before departure.</p>}
    </div>
  );
}

export default function TrackPage() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get(`/public/track/${code}`).then((r) => { setD(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))), [code]);
  useEffect(() => {
    setD(null);
    if (!code) return undefined;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [code, load]);

  if (!code || (err && !d)) return <div className="hero-bg min-h-full px-4 py-12"><TrackForm initial={code} err={err} /></div>;
  if (!d) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#0052FF]" /></div>;
  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6" data-testid="track-page">
      <div className="grid gap-5 lg:grid-cols-[1fr_380px]">
        <LiveMap buses={[d]} selected={d.trip_id} className="h-[360px] lg:h-[620px]" />
        <div className="space-y-4">
          <Summary d={d} />
          <div className="card-soft p-5"><div className="label-xs mb-3">Stops</div><StopTimeline timeline={d.timeline} fromIdx={d.from_idx} toIdx={d.to_idx} /></div>
          <p className="text-center text-xs text-slate-400">Updates every 15 seconds</p>
        </div>
      </div>
    </div>
  );
}
